import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, MessageSquare, X, ChevronRight } from "lucide-react";
import toast from "react-hot-toast";
import {
  FetchMyAdminInquiries,
  CreateAdminInquiry,
  ClearAdminInquiryAction,
} from "../../Store/Actions/AdminInquiryActions";

const STATUS_STYLES = {
  OPEN: "bg-green-100 text-green-700",
  IN_PROGRESS: "bg-yellow-100 text-yellow-700",
  CLOSED: "bg-gray-100 text-gray-600",
};

const STATUS_LABELS = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  CLOSED: "Closed",
};

function formatDate(isoString) {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function MainAdminInquiries() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { inquiries, loader, actionSuccess, actionError } = useSelector(
    (state) => state.adminInquiry
  );

  const [showModal, setShowModal] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const actionHandled = useRef(false);

  useEffect(() => {
    dispatch(FetchMyAdminInquiries());
  }, [dispatch]);

  // Handle action feedback
  useEffect(() => {
    if (actionSuccess && !actionHandled.current) {
      actionHandled.current = true;
      toast.success("Inquiry submitted successfully.");
      dispatch(ClearAdminInquiryAction());
      setSubmitting(false);
      closeModal();
    }
  }, [actionSuccess, dispatch]);

  useEffect(() => {
    if (actionError && !actionHandled.current) {
      actionHandled.current = true;
      toast.error(actionError.message || "Failed to submit inquiry.");
      dispatch(ClearAdminInquiryAction());
      setSubmitting(false);
    }
  }, [actionError, dispatch]);

  function openModal() {
    setSubject("");
    setMessage("");
    setErrors({});
    actionHandled.current = false;
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setSubject("");
    setMessage("");
    setErrors({});
  }

  function validate() {
    const e = {};
    if (!subject.trim()) e.subject = "Subject is required.";
    if (!message.trim()) e.message = "Message is required.";
    return e;
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    actionHandled.current = false;
    setSubmitting(true);
    dispatch(CreateAdminInquiry(subject.trim(), message.trim()));
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-popinsSemiBold text-gray-800">Admin Inquiries</h1>
          <p className="text-sm text-gray-500 font-popinsRegular mt-1">
            Submit and track your inquiries to the TCC admin team.
          </p>
        </div>
        <button
          onClick={openModal}
          className="flex items-center gap-2 bg-themeColor text-white px-5 py-2.5 rounded-lg text-sm font-popinsMedium hover:bg-opacity-90 transition"
        >
          <Plus size={16} />
          New Inquiry
        </button>
      </div>

      {/* Inquiries List */}
      {loader && inquiries.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 size={32} className="animate-spin text-themeColor" />
        </div>
      ) : inquiries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400">
          <MessageSquare size={48} className="mb-4 opacity-40" />
          <p className="text-base font-popinsMedium">No inquiries yet</p>
          <p className="text-sm font-popinsRegular mt-1">
            Click "New Inquiry" to contact the admin team.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Subject
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Messages
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden md:table-cell">
                    Last Message
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-popinsSemiBold text-gray-500 uppercase tracking-wide hidden lg:table-cell">
                    Date
                  </th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {inquiries.map((inq) => (
                  <tr
                    key={inq.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/dashboard/admin-inquiries/${inq.id}`)}
                  >
                    <td className="px-6 py-4">
                      <span className="font-popinsMedium text-gray-800 line-clamp-1">
                        {inq.subject}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full bg-themeColor text-white text-xs font-popinsMedium">
                        {inq.messageCount ?? 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-block text-xs font-popinsMedium px-2.5 py-0.5 rounded-full ${
                          STATUS_STYLES[inq.status] ?? "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {STATUS_LABELS[inq.status] ?? inq.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-popinsRegular hidden md:table-cell">
                      <span className="line-clamp-1 max-w-[240px]">
                        {inq.lastMessage || "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-500 font-popinsRegular hidden lg:table-cell">
                      {formatDate(inq.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <ChevronRight size={16} className="text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New Inquiry Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
              <h2 className="text-base font-popinsSemiBold text-gray-800">New Inquiry</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-popinsMedium text-gray-700 mb-1">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => {
                    setSubject(e.target.value);
                    if (errors.subject) setErrors((p) => ({ ...p, subject: null }));
                  }}
                  placeholder="e.g. Question about my account"
                  maxLength={200}
                  className={`w-full border rounded-lg px-4 py-2.5 text-sm font-popinsRegular focus:outline-none focus:ring-2 focus:ring-themeColor ${
                    errors.subject ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.subject && (
                  <p className="mt-1 text-xs text-red-500">{errors.subject}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-popinsMedium text-gray-700 mb-1">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    if (errors.message) setErrors((p) => ({ ...p, message: null }));
                  }}
                  placeholder="Describe your inquiry in detail…"
                  rows={5}
                  className={`w-full border rounded-lg px-4 py-2.5 text-sm font-popinsRegular focus:outline-none focus:ring-2 focus:ring-themeColor resize-none ${
                    errors.message ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {errors.message && (
                  <p className="mt-1 text-xs text-red-500">{errors.message}</p>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 py-2 rounded-lg text-sm font-popinsMedium text-gray-600 border border-gray-200 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-popinsMedium bg-themeColor text-white hover:bg-opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {submitting ? "Submitting…" : "Submit Inquiry"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
